import React from 'react';
import { Calendar, Clock, Users, Mail, MapPin, Video } from 'lucide-react';
// Loading component
const LoadingView = () => (React.createElement("div", { className: "bg-white rounded-md border border-gray-200 p-3" },
    React.createElement("div", { className: "flex items-center space-x-2" },
        React.createElement("div", { className: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" }),
        React.createElement("span", { className: "text-gray-600 text-sm" }, "Loading..."))));
// Single meeting card component
const SingleMeetingCard = ({ date, time, meetingName, description, isOnlineMeeting, location, attendees, organizer }) => {
    // Function to strip HTML tags and clean text
    const stripHtmlAndClean = (html) => {
        if (!html)
            return '';
        // Remove HTML tags
        const textOnly = html.replace(/<[^>]*>/g, '');
        // Remove extra whitespace and line breaks
        const cleaned = textOnly.replace(/\s+/g, ' ').trim();
        // Return empty if it's too short or contains mostly HTML artifacts
        if (cleaned.length < 10 || cleaned.includes('&nbsp;') || cleaned.includes('&lt;')) {
            return '';
        }
        return cleaned;
    };
    // Clean the description
    const cleanDescription = description ? stripHtmlAndClean(description) : '';
    const shouldShowDescription = cleanDescription && cleanDescription.length > 0;
    return (React.createElement("div", { className: "bg-gradient-to-br from-purple-50 via-purple-50 to-indigo-100 rounded-lg p-3 shadow-sm border border-purple-100 mb-3 max-w-full overflow-hidden" },
        React.createElement("div", { className: "flex items-center space-x-2 mb-2" },
            React.createElement("div", { className: "flex-shrink-0" },
                React.createElement(Calendar, { className: "w-4 h-4 text-blue-600" })),
            React.createElement("div", null,
                React.createElement("h3", { className: "text-xs font-medium text-gray-700" }, date))),
        React.createElement("h2", { className: "text-base font-bold text-gray-900 mb-2 break-words" }, meetingName),
        React.createElement("div", { className: "mb-3" },
            React.createElement("p", { className: "text-gray-900 font-medium text-sm" },
                React.createElement("span", { className: "text-gray-700" }, "Time:"),
                " ",
                time),
            isOnlineMeeting && (React.createElement("div", { className: "flex items-center space-x-1 mt-1" },
                React.createElement(Video, { className: "w-3 h-3 text-blue-600" }),
                React.createElement("span", { className: "text-blue-600 font-medium text-xs" }, "Online")))),
        shouldShowDescription && (React.createElement("div", { className: "mb-3" },
            React.createElement("p", { className: "text-gray-600 leading-relaxed text-xs break-words" }, cleanDescription))),
        React.createElement("div", { className: "space-y-1" },
            organizer && (React.createElement("div", { className: "flex items-center space-x-1 text-gray-600 text-xs" },
                React.createElement(Mail, { className: "w-3 h-3" }),
                React.createElement("span", { className: "font-medium" },
                    "Organized by ",
                    organizer))),
            attendees && attendees > 0 && (React.createElement("div", { className: "flex items-center space-x-1 text-gray-600 text-xs" },
                React.createElement(Users, { className: "w-3 h-3" }),
                React.createElement("span", { className: "font-medium" },
                    attendees,
                    " attendee",
                    attendees !== 1 ? 's' : ''))),
            location && (React.createElement("div", { className: "flex items-center space-x-1 text-gray-600 text-xs" },
                React.createElement(MapPin, { className: "w-3 h-3" }),
                React.createElement("span", { className: "font-medium" }, location))))));
};
// Type guard functions
const isSingleMeetingProps = (props) => {
    return 'meetingName' in props;
};
const isMultipleMeetingsProps = (props) => {
    return 'meetings' in props;
};
export const CalendarMeetingCard = (props) => {
    // Single meeting display
    if (isSingleMeetingProps(props)) {
        return React.createElement(SingleMeetingCard, Object.assign({}, props));
    }
    // Multiple meetings display
    if (isMultipleMeetingsProps(props)) {
        const { meetings, title = "Upcoming Meetings" } = props;
        const formatDateTime = (dateTimeString) => {
            const date = new Date(dateTimeString);
            return {
                date: date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                }),
                time: date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }),
            };
        };
        const getDuration = (start, end) => {
            const startTime = new Date(start);
            const endTime = new Date(end);
            const durationMs = endTime.getTime() - startTime.getTime();
            const hours = Math.floor(durationMs / (1000 * 60 * 60));
            const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
            if (hours > 0) {
                return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
            }
            return `${minutes}m`;
        };
        if (!meetings || meetings.length === 0) {
            return (React.createElement("div", { className: "bg-white rounded-md border border-gray-200 p-4 text-center" },
                React.createElement(Calendar, { className: "w-8 h-8 text-gray-400 mx-auto mb-2" }),
                React.createElement("h3", { className: "text-sm font-medium text-gray-900 mb-1" }, "No meetings found"),
                React.createElement("p", { className: "text-gray-500 text-xs" }, "No upcoming meetings found.")));
        }
        return (React.createElement("div", { className: "space-y-3 max-w-full" },
            React.createElement("div", { className: "flex items-center space-x-2 mb-3" },
                React.createElement(Calendar, { className: "w-4 h-4 text-blue-600" }),
                React.createElement("h3", { className: "text-base font-semibold text-gray-900 break-words flex-1" }, title),
                React.createElement("span", { className: "bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0" }, meetings.length)),
            React.createElement("div", { className: "space-y-2" }, meetings.map((meeting, index) => {
                var _a, _b;
                const startDateTime = formatDateTime(meeting.start.dateTime);
                const endDateTime = formatDateTime(meeting.end.dateTime);
                const duration = getDuration(meeting.start.dateTime, meeting.end.dateTime);
                const attendeesCount = ((_a = meeting.attendees) === null || _a === void 0 ? void 0 : _a.length) || 0;
                return (React.createElement("div", { key: meeting.id || index, className: "bg-white rounded-md border border-gray-200 p-3 hover:shadow-md transition-shadow max-w-full overflow-hidden" },
                    React.createElement("div", { className: "flex items-start justify-between mb-2" },
                        React.createElement("h4", { className: "text-sm font-medium text-gray-900 line-clamp-2 flex-1 break-words pr-2" }, meeting.subject || 'Untitled Meeting'),
                        meeting.isOnlineMeeting && (React.createElement("div", { className: "flex-shrink-0 ml-2" },
                            React.createElement(Video, { className: "w-3 h-3 text-blue-600" })))),
                    React.createElement("div", { className: "flex items-center space-x-3 mb-2 text-xs" },
                        React.createElement("div", { className: "flex items-center space-x-1 text-gray-600" },
                            React.createElement(Clock, { className: "w-3 h-3" }),
                            React.createElement("span", null, startDateTime.date)),
                        React.createElement("div", { className: "text-gray-900 font-medium" },
                            startDateTime.time,
                            " - ",
                            endDateTime.time),
                        React.createElement("div", { className: "text-gray-500" },
                            "(",
                            duration,
                            ")")),
                    React.createElement("div", { className: "space-y-1" },
                        meeting.organizer && (React.createElement("div", { className: "flex items-center space-x-1 text-xs text-gray-600" },
                            React.createElement(Mail, { className: "w-3 h-3" }),
                            React.createElement("span", null,
                                "Organized by ",
                                meeting.organizer.emailAddress.name))),
                        attendeesCount > 0 && (React.createElement("div", { className: "flex items-center space-x-1 text-xs text-gray-600" },
                            React.createElement(Users, { className: "w-3 h-3" }),
                            React.createElement("span", null,
                                attendeesCount,
                                " attendee",
                                attendeesCount !== 1 ? 's' : ''))),
                        ((_b = meeting.location) === null || _b === void 0 ? void 0 : _b.displayName) && (React.createElement("div", { className: "flex items-center space-x-1 text-xs text-gray-600" },
                            React.createElement(MapPin, { className: "w-3 h-3" }),
                            React.createElement("span", null, meeting.location.displayName))),
                        meeting.isOnlineMeeting && meeting.onlineMeetingUrl && (React.createElement("div", { className: "flex items-center space-x-1 text-xs" },
                            React.createElement(Video, { className: "w-3 h-3 text-blue-600" }),
                            React.createElement("a", { href: meeting.onlineMeetingUrl, target: "_blank", rel: "noopener noreferrer", className: "text-blue-600 hover:text-blue-800 hover:underline" }, "Join online meeting"))))));
            }))));
    }
    return null;
};
// Export LoadingView for external use
export { LoadingView };
// Export the component with alternative name for compatibility
export const CalendarMeetingCardComponent = CalendarMeetingCard;
